/**
 * Inline fix-up for a company name the ownership resolver could not match to a
 * parent organization: create it, or record it as another spelling of one that
 * exists.
 *
 * Both actions WRITE immediately — a single-doc create or a single-doc update —
 * because the point is to fix the catalogue, not just this row. The name then
 * resolves on every later row in the same review and on every future enrich
 * run, which is why the proposed spelling always ends up in `alternativeNames`.
 *
 * Creating is behind a confirm step that shows the exact document id. The name
 * came from the MODEL, and the easiest mistake here is minting a parent
 * organization for something that is really a brand.
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import type { ParentOrganization } from '../../types';
import { createParentOrganization, updateParentOrganization } from '../../services/firebase';
import { EntityPicker, optionMatches, type EntityOption } from './EntityPicker';
import {
  planQuickCreate,
  quickCreateDoc,
  withAlias,
  type QuickCreateDetails,
} from './quickCreateParentOrg';

interface Props {
  /** The unmatched name the model proposed. */
  name: string;
  /** Every organization known this session — Firestore's plus any just created. */
  orgs: ParentOrganization[];
  /** Called with the organization the row should now point at, as it now stands. */
  onResolved: (org: ParentOrganization) => void;
  disabled?: boolean;
  /** Distinguishes the controls of one row from another's for assistive tech. */
  ariaContext: string;
}

type Mode = 'idle' | 'confirmCreate' | 'pickAlias';

/** The mini-form's fields. Strings throughout, so blank means "unset". */
interface DetailsForm {
  name: string;
  displayName: string;
  country: string;
  founded: string;
}

export function QuickOrgResolve({ name, orgs, onResolved, disabled = false, ariaContext }: Props) {
  const [mode, setMode] = useState<Mode>('idle');
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<DetailsForm>({
    name,
    displayName: '',
    country: '',
    founded: '',
  });

  // Re-planned against the form's name, not the proposed one: editing the name
  // moves the document id, and the curator has to see the id they are actually
  // about to create — including when the correction collides with an org that
  // already exists, which is the common case for a name typed by hand.
  const plan = planQuickCreate(form.name, orgs);
  const off = disabled || busy;

  const openCreate = () => {
    setForm({ name, displayName: '', country: '', founded: '' });
    setMode('confirmCreate');
  };

  /**
   * Add `name` to `org`'s aliases and point the row at it. When the org already
   * answers to the name there is nothing to write — the row is simply pointed at
   * it, which is what the curator asked for.
   */
  const addAlias = async (org: ParentOrganization) => {
    const nextNames = withAlias(org, name);
    if (nextNames === null) {
      onResolved(org);
      setMode('idle');
      toast.success(`Using ${org.displayName || org.name}`);
      return;
    }
    setBusy(true);
    try {
      await updateParentOrganization(org.id, { alternativeNames: nextNames });
      onResolved({ ...org, alternativeNames: nextNames });
      setMode('idle');
      toast.success(`Added “${name}” to ${org.displayName || org.name}`);
    } catch (err) {
      console.error('Failed to add alias:', err);
      toast.error('Could not save that alias.');
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (plan.kind !== 'create') return;
    setBusy(true);
    try {
      const founded = Number.parseInt(form.founded.trim(), 10);
      const details: QuickCreateDetails = {
        displayName: form.displayName,
        country: form.country,
        ...(Number.isFinite(founded) ? { founded } : {}),
        // The name the row could not resolve, kept as an alias even when the
        // curator renamed the organization in the form.
        extraAliases: [name],
      };
      const doc = quickCreateDoc(plan, details);
      const created = await createParentOrganization(plan.id, doc);
      if (!created) {
        // Taken between planning and writing — by another tab, or another row's
        // quick-create in this very dialog. Not an error: surface it as the
        // collision it now is, and let the curator choose the alias route.
        toast.error(`${plan.id} was just created elsewhere — add as an alias instead`);
        setMode('pickAlias');
        return;
      }
      onResolved({ id: plan.id, ...doc } as ParentOrganization);
      setMode('idle');
      toast.success(`Created ${plan.id}`);
    } catch (err) {
      console.error('Failed to create parent organization:', err);
      toast.error('Could not create that organization.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'confirmCreate') {
    return (
      <div className="mt-1 rounded border border-amber-200 bg-amber-50 p-2 text-[11px]">
        {/* The name lives ABOVE the branches because it drives them: editing it
            re-plans, so the panel flips between "creates this id" and "that id
            is taken" as the curator types, instead of making them cancel and
            start again to find out. */}
        <div className="grid grid-cols-2 gap-1.5">
          <MiniField
            label="Name"
            value={form.name}
            onChange={(v) => setForm((p) => ({ ...p, name: v }))}
            ariaLabel={`Name for new organization for ${ariaContext}`}
            disabled={off}
          />
          <MiniField
            label="Display name"
            value={form.displayName}
            onChange={(v) => setForm((p) => ({ ...p, displayName: v }))}
            placeholder={form.name.trim()}
            ariaLabel={`Display name for new organization for ${ariaContext}`}
            disabled={off}
          />
          <MiniField
            label="Country"
            value={form.country}
            onChange={(v) => setForm((p) => ({ ...p, country: v }))}
            ariaLabel={`Country for new organization for ${ariaContext}`}
            disabled={off}
          />
          <MiniField
            label="Founded"
            value={form.founded}
            onChange={(v) => setForm((p) => ({ ...p, founded: v.replace(/[^0-9]/g, '') }))}
            placeholder="e.g. 1981"
            ariaLabel={`Founded year for new organization for ${ariaContext}`}
            disabled={off}
          />
        </div>
        {plan.kind === 'create' && (
          <>
            <p className="mt-1.5 text-gray-700">
              Creates <code className="font-mono text-gray-900">parent_organizations/{plan.id}</code>.
              Make sure this is an organization and not a brand. Anything left blank stays unset.
            </p>
            <div className="mt-1.5 flex gap-1.5">
              <SmallButton onClick={() => void create()} disabled={off} primary>
                {busy ? 'Creating…' : 'Create'}
              </SmallButton>
              <SmallButton onClick={() => setMode('idle')} disabled={busy}>
                Cancel
              </SmallButton>
            </div>
          </>
        )}
        {plan.kind === 'collision' && (
          <>
            <p className="mt-1.5 text-gray-700">
              <code className="font-mono text-gray-900">{plan.id}</code> already exists
              {' '}({plan.existing.label}) —{' '}
              {plan.alreadyKnown ? 'and already answers to this name.' : 'add as AKA instead?'}
            </p>
            <div className="mt-1.5 flex gap-1.5">
              <SmallButton
                onClick={() => {
                  const existing = orgs.find((o) => o.id === plan.existing.id);
                  if (existing) void addAlias(existing);
                }}
                disabled={off}
                primary
              >
                {plan.alreadyKnown ? `Use ${plan.existing.label}` : `Add as AKA to ${plan.existing.label}`}
              </SmallButton>
              <SmallButton onClick={() => setMode('idle')} disabled={busy}>
                Cancel
              </SmallButton>
            </div>
          </>
        )}
        {plan.kind === 'unusable' && (
          <p className="mt-1.5 text-gray-700">
            This name has no letters or digits to make an id from.{' '}
            <SmallButton onClick={() => setMode('idle')}>Back</SmallButton>
          </p>
        )}
      </div>
    );
  }

  if (mode === 'pickAlias') {
    const options: EntityOption[] = orgs
      .map((o) => ({ id: o.id, label: o.displayName || o.name || o.id, sub: o.country }))
      .filter((o) => optionMatches(o, query));
    return (
      <div className="mt-1 space-y-1">
        <p className="text-[11px] text-gray-600">Add “{name}” as another name for:</p>
        <EntityPicker
          value=""
          options={options}
          query={query}
          onQueryChange={setQuery}
          onSelect={(id) => {
            const org = orgs.find((o) => o.id === id);
            if (org) void addAlias(org);
          }}
          ariaLabel={`Organization to alias for ${ariaContext}`}
          placeholder="Search parent organizations…"
          disabled={off}
        />
        <SmallButton onClick={() => setMode('idle')} disabled={busy}>
          Cancel
        </SmallButton>
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      <SmallButton
        onClick={openCreate}
        disabled={off}
        ariaLabel={`Create as parent-org for ${ariaContext}`}
      >
        Create as parent-org
      </SmallButton>
      <SmallButton
        onClick={() => {
          setQuery('');
          setMode('pickAlias');
        }}
        disabled={off}
        ariaLabel={`Add as AKA to existing org for ${ariaContext}`}
      >
        Add as AKA to existing org
      </SmallButton>
    </div>
  );
}

/** One labelled input in the create form. Blank means unset, never a default. */
function MiniField({
  label,
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <input
        type="text"
        value={value}
        aria-label={ariaLabel}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-[11px] text-gray-800 disabled:bg-gray-50 disabled:text-gray-400"
      />
    </label>
  );
}

function SmallButton({
  children,
  onClick,
  disabled,
  primary = false,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-50 ${
        primary
          ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}
